import React from 'react';
import {
  Table,
  Group,
  Text,
  Badge,
  ActionIcon,
  Menu,
  Avatar,
  Stack,
  Tooltip,
} from '@mantine/core';
import {
  IconEdit,
  IconTrash,
  IconLock,
  IconLockOpen,
  IconDotsVertical,
  IconMail,
  IconPhone,
  IconShield,
  IconCrown,
  IconStar,
  IconUser,
} from '@tabler/icons-react';
import { User, UserRole } from '../../../types';

interface UserTableProps {
  users: User[];
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  onToggleStatus: (user: User) => void;
  canEdit?: boolean;
  canDelete?: boolean;
  canChangeStatus?: boolean;
  currentUserId?: string;
}

export const UserTable: React.FC<UserTableProps> = ({
  users,
  onEdit,
  onDelete,
  onToggleStatus,
  canEdit = true,
  canDelete = true,
  canChangeStatus = true,
  currentUserId,
}) => {
  // Role badge color mapping
  const getRoleBadgeColor = (role: string): string => {
    switch (role) {
      case UserRole.ADMIN: return 'red';
      case UserRole.DIRECTOR: return 'grape';
      case UserRole.MANAGER: return 'blue';
      case UserRole.USER: return 'green';
      default: return 'gray';
    }
  };

  // Role icon mapping
  const getRoleIcon = (role: string) => {
    switch (role) {
      case UserRole.ADMIN: return <IconCrown size={14} />;
      case UserRole.DIRECTOR: return <IconStar size={14} />;
      case UserRole.MANAGER: return <IconShield size={14} />;
      case UserRole.USER: return <IconUser size={14} />;
      default: return null;
    }
  };

  // Role display name mapping
  const getRoleDisplayName = (role: string): string => {
    switch (role) {
      case UserRole.ADMIN: return 'Admin';
      case UserRole.DIRECTOR: return 'Director';
      case UserRole.MANAGER: return 'Manager';
      case UserRole.USER: return 'User';
      default: return role.replace('ROLE_', '');
    }
  };

  if (users.length === 0) {
    return (
      <div className="text-center p-8">
        <Text c="dimmed" size="lg">
          No users found matching your criteria.
        </Text>
      </div>
    );
  }

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>User</Table.Th>
          <Table.Th>Contact</Table.Th>
          <Table.Th>Roles</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>2FA</Table.Th>
          <Table.Th>Verified</Table.Th>
          <Table.Th>Created</Table.Th>
          <Table.Th>Actions</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {users.map((user) => {
          const isCurrentUser = user.id === currentUserId;
          const canEditUser = canEdit && (!isCurrentUser || user.roles.includes(UserRole.ADMIN));
          const canDeleteUser = canDelete && !isCurrentUser;
          const canToggleStatus = canChangeStatus && !isCurrentUser;
          
          return (
            <Table.Tr key={user.id}>
              <Table.Td>
                <Group gap="sm">
                  <Avatar 
                    color={user.enabled ? "exportGreen" : "gray"} 
                    radius="xl"
                    style={{ 
                      opacity: user.enabled ? 1 : 0.6,
                      border: `2px solid ${user.enabled ? '#2E7D32' : '#9E9E9E'}`
                    }}
                  >
                    {user.fullName.charAt(0).toUpperCase()}
                  </Avatar>
                  <div>
                    <Group gap="xs">
                      <Text fw={500}>{user.fullName}</Text>
                      {isCurrentUser && (
                        <Badge size="xs" color="blue" variant="light">You</Badge>
                      )}
                    </Group>
                    <Text size="xs" c="dimmed">@{user.username}</Text>
                  </div>
                </Group>
              </Table.Td>
              <Table.Td>
                <Stack gap={5}>
                  <Group gap="xs" wrap="nowrap">
                    <IconMail size={14} style={{ flexShrink: 0 }} />
                    <Text size="sm" style={{ wordBreak: 'break-all' }}>
                      {user.email}
                    </Text>
                  </Group>
                  <Group gap="xs" wrap="nowrap">
                    <IconPhone size={14} style={{ flexShrink: 0 }} />
                    <Text size="sm">{user.phoneNumber}</Text>
                  </Group>
                </Stack>
              </Table.Td>
              <Table.Td>
                <Group gap="xs">
                  {user.roles.map((role) => (
                    <Tooltip key={role} label={getRoleDisplayName(role)} withArrow>
                      <Badge 
                        color={getRoleBadgeColor(role)} 
                        variant="light"
                        leftSection={getRoleIcon(role)}
                      >
                        {getRoleDisplayName(role)}
                      </Badge>
                    </Tooltip>
                  ))}
                </Group>
              </Table.Td>
              <Table.Td>
                <Badge 
                  color={user.enabled ? 'green' : 'red'} 
                  variant="light"
                  leftSection={user.enabled ? <IconLockOpen size={12} /> : <IconLock size={12} />}
                >
                  {user.enabled ? 'Active' : 'Locked'}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Badge 
                  color={user.twoFactorEnabled ? 'blue' : 'gray'} 
                  variant="light"
                >
                  {user.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Group gap={5}>
                  {user.emailVerified && (
                    <Badge color="green" size="xs" variant="light">Email</Badge>
                  )}
                  {user.phoneVerified && (
                    <Badge color="blue" size="xs" variant="light">Phone</Badge>
                  )}
                  {!user.emailVerified && !user.phoneVerified && (
                    <Text size="xs" c="dimmed">-</Text>
                  )}
                </Group>
              </Table.Td>
              <Table.Td>
                <Tooltip label={new Date(user.createdAt).toLocaleString()} withArrow>
                  <Text size="sm">{new Date(user.createdAt).toLocaleDateString()}</Text>
                </Tooltip>
              </Table.Td>
              <Table.Td>
                <Menu shadow="md" width={200} position="bottom-start">
                  <Menu.Target>
                    <ActionIcon variant="subtle" size="sm">
                      <IconDotsVertical size={16} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {canEditUser && (
                      <Menu.Item
                        leftSection={<IconEdit size={14} />}
                        onClick={() => onEdit(user)}
                      >
                        Edit User
                      </Menu.Item>
                    )}
                    
                    {canToggleStatus && (
                      <Menu.Item
                        leftSection={user.enabled ? <IconLock size={14} /> : <IconLockOpen size={14} />}
                        onClick={() => onToggleStatus(user)}
                        color={user.enabled ? 'orange' : 'green'}
                      >
                        {user.enabled ? 'Lock User' : 'Unlock User'}
                      </Menu.Item>
                    )}
                    
                    {canDeleteUser && (
                      <>
                        <Menu.Divider />
                        <Menu.Item
                          leftSection={<IconTrash size={14} />}
                          color="red"
                          onClick={() => onDelete(user)}
                        >
                          Delete User
                        </Menu.Item>
                      </>
                    )}
                    
                    {!canEditUser && !canToggleStatus && !canDeleteUser && (
                      <Menu.Item disabled>
                        <Text size="xs" c="dimmed">No actions available</Text>
                      </Menu.Item>
                    )}
                  </Menu.Dropdown>
                </Menu>
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
};